import { createThreadStarter } from './services/thread-start.mjs';
import { renderAppTabShell } from './components/app-tab-shell.mjs';
import {
  conditionOptions,
  sellerCategories,
} from './demo-content.mjs';
import { renderAuthWelcomeScreen } from './features/auth/welcome-screen.mjs';
import {
  renderPhoneInputScreen,
  resolveDefaultPhonePrefix,
} from './features/auth/phone-input-screen.mjs';
import { renderOtpScreen } from './features/auth/otp-screen.mjs';
import {
  renderLegalLinks,
  renderLegalStatusNotice,
  renderTermsAcceptanceScreen,
} from './features/auth/terms-acceptance-screen.mjs';
import { renderInboxScreen } from './features/chat/inbox-screen.mjs';
import { createChatLiveRefreshController } from './features/chat/chat-live-refresh-controller.mjs';
import { renderThreadScreen } from './features/chat/thread-screen.mjs';
import { renderBuyScreen } from './features/home/buy-screen.mjs';
import {
  createBuyerBrowseController,
  getRenderableRouteKey,
  parseAppRoute,
} from './features/home/buyer-browse-controller.mjs';
import { renderHomeScreen } from './features/home/home-screen.mjs';
import { renderListingDetailScreen } from './features/listings/listing-detail-screen.mjs';
import { renderCaptureScreen } from './features/post/capture-screen.mjs';
import { renderCaptureResultScreen } from './features/post/capture-result-screen.mjs';
import { renderPhotoGuidanceScreen } from './features/post/photo-guidance-screen.mjs';
import { renderPublishGateScreen } from './features/post/publish-gate-screen.mjs';
import { renderReviewFormScreen } from './features/post/review-form-screen.mjs';
import { renderSuccessScreen } from './features/post/success-screen.mjs';
import { createUploadTaskQueue } from './features/post/upload-task-queue.mjs';
import {
  renderProfileCityFeedback,
  renderProfileScreen,
} from './features/profile/profile-screen.mjs';
import { renderSellerPublicScreen } from './features/profile/seller-public-screen.mjs';
import { renderWalletScreen } from './features/wallet/wallet-screen.mjs';
import { submitLivePublish } from './features/post/live-publish-flow.mjs';
import { getCategoryGuidance } from './models/category-guidance.mjs';
import {
  createEditableListingDraft,
  markDraftOtpVerified,
  updateListingDraft,
} from './models/listing-draft.mjs';
import { createAiDraftService } from './services/ai-draft.mjs';
import { createApiConfig } from './services/api-config.mjs';
import { createAuthService } from './services/auth-service.mjs';
import { createChatService } from './services/chat-service.mjs';
import { createLegalAwareFetch } from './services/legal-api-fetch.mjs';
import { createDraftStorageService } from './services/draft-storage.mjs';
import { createImageCompressionService } from './services/image-compression.mjs';
import { createListingsService } from './services/listings-service.mjs';
import { createMediaService } from './services/media-service.mjs';
import { createProfileService } from './services/profile-service.mjs';
import { createReviewReportsService } from './services/review-reports-service.mjs';
import { createReviewsService } from './services/reviews-service.mjs';
import { createSellerListingsService } from './services/seller-listings-service.mjs';
import { createSellerRepliesService } from './services/seller-replies-service.mjs';
import { createWalletService } from './services/wallet-service.mjs';
import { createLiveDraftService } from './services/live-draft-service.mjs';
import {
  captureBuyerSearchRenderState,
  restoreBuyerSearchRenderState,
} from './utils/buyer-search-render-state.mjs';
import {
  captureBuyerCategoryScrollRenderState,
  restoreBuyerCategoryScrollRenderState,
} from './utils/buyer-category-scroll-render-state.mjs';
import { resolveDiscardDraftRoute } from './utils/draft-discard-navigation.mjs';
import { resolvePhoneCountry } from './utils/phone-country.mjs';
import { buildWhatsAppChatLink } from './utils/whatsapp-link.mjs';
import {
  captureReviewDraftRenderState,
  restoreReviewDraftRenderState,
} from './utils/review-draft-render-state.mjs';
import {
  captureThreadComposerRenderState,
  restoreThreadComposerRenderState,
} from './utils/thread-composer-render-state.mjs';
import { syncUnreadMessagesBadge } from './utils/unread-badge-dom.mjs';
import {
  captureProfileCityRenderState,
  restoreProfileCityRenderState,
} from './utils/profile-city-render-state.mjs';
import {
  captureScrollRenderState,
  restoreScrollRenderState,
} from './utils/scroll-render-state.mjs';
import { createPendingScrollResetController } from './utils/pending-scroll-reset.mjs';
import { createSearchSignalReporter } from './utils/search-signal-reporter.mjs';
import { syncDraftAreaFromProfile } from './utils/profile-area-sync.mjs';
import { deriveProfileCityAutocompleteState } from './utils/profile-city-autocomplete-state.mjs';
import { resolveProfileCityHydration } from './utils/profile-city-hydration.mjs';
import { resolveProfileAreaForSubmit } from './utils/profile-zone-submit.mjs';
import { resolveDisplayNameForSubmit } from './utils/profile-identity-submit.mjs';
import {
  formatPricePreview,
  getPriceInputPlaceholder,
  normalizePriceCurrency,
} from './utils/price-input.mjs';
import { buildReviewDraftDetails } from './utils/review-draft-form.mjs';
import {
  resolveDraftlessSellerRoute,
  shouldRetainDraftAfterPublish,
} from './utils/post-publish-draft-state.mjs';
import {
  createPostFlowController,
  decidePublishGate,
  getMissingRequiredPhotoPrompts,
  refreshReviewValidationErrors,
  validateDraftForPublish,
} from './features/post/post-flow-controller.mjs';
import { renderShareMenu } from './components/share-menu.mjs';
import { createListingShareController } from './services/listing-share.mjs';
import { renderCountrySuggestionBanner } from './components/country-banner.mjs';
import {
  createCountryPreference,
  readCountryFromSearch,
  readGeoCountry,
  stripCountrySearchParam,
} from './services/country-preference.mjs';

const appRoot = document.querySelector('[data-app-root]');

if (appRoot) {
  const apiConfig = createApiConfig({
    globalObject: window,
  });
  const draftStorage = createDraftStorageService({
    storage: window.localStorage,
  });
  const authService = createAuthService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: window.fetch.bind(window),
    storage: window.localStorage,
  });
  // A 428 on an account request means the server published a version this
  // account has not accepted: reload the authoritative status, never assume it.
  const apiFetch = createLegalAwareFetch({
    fetchFn: window.fetch.bind(window),
    getSessionToken: () => state.session?.sessionToken ?? '',
    onTermsRequired: () => {
      state.legalStatusStatus = 'idle';
      void loadLegalStatus();
    },
  });
  const listingsService = createListingsService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const searchSignalReporter = createSearchSignalReporter({
    reportFn: listingsService.reportSearchQuery.bind(listingsService),
  });
  const reviewsService = createReviewsService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const reviewReportsService = createReviewReportsService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const sellerRepliesService = createSellerRepliesService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const chatService = createChatService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const walletService = createWalletService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const sellerListingsService = createSellerListingsService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const profileService = createProfileService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const mediaService = createMediaService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const liveDraftService = createLiveDraftService({
    apiBaseUrl: apiConfig.apiBaseUrl,
    fetchFn: apiFetch,
  });
  const countryPreference = createCountryPreference({ storage: window.localStorage });
  const countryFromSearch = readCountryFromSearch(window.location.search);
  if (countryFromSearch) {
    countryPreference.setStoredCountry(countryFromSearch);
    window.history.replaceState(null, '', stripCountrySearchParam(window.location));
  }
  const photoUploadQueue = createUploadTaskQueue({
    onStateChange: () => {
      renderApp();
    },
  });
  const pendingScrollReset = createPendingScrollResetController();
  const chatLiveRefreshController = createChatLiveRefreshController();
  function buildUploadProgress(flow, stage) {
    if (!flow || !stage) {
      return null;
    }

    const flowConfig =
      flow === 'guided'
        ? {
            title: 'Ajout de la photo guidée',
            stages: ['compressing', 'uploading', 'complete'],
          }
        : {
            title: 'Préparation de la photo',
            stages: ['compressing', 'uploading', 'analyzing'],
          };
    const labels = {
      analyzing: 'Analyse IA',
      complete: 'Terminé',
      compressing: 'Compression',
      uploading: 'Téléversement',
    };
    const activeIndex = Math.max(flowConfig.stages.indexOf(stage), 0);

    return {
      activeStage: stage,
      stages: flowConfig.stages.map((stageId, index) => ({
        id: stageId,
        label: labels[stageId] ?? stageId,
        state:
          index < activeIndex ? 'complete' : index === activeIndex ? 'active' : 'pending',
      })),
      title: flowConfig.title,
    };
  }

  function syncUploadProgress(progressState) {
    if (!progressState) {
      state.uploadProgress = null;
      renderApp();
      return;
    }

    state.uploadProgress = buildUploadProgress(progressState.flow, progressState.stage);
    renderApp();
  }

  const postFlowController = createPostFlowController({
    draftStorage,
    imageCompressionService: createImageCompressionService(),
    aiDraftService: createAiDraftService({
      apiBaseUrl: apiConfig.apiBaseUrl,
      fetchFn: apiFetch,
    }),
    mediaService,
    onUploadStageChange: syncUploadProgress,
    createPreviewUrl: (file) => window.URL.createObjectURL(file),
  });
  const buyerBrowseController = createBuyerBrowseController({
    listingsService,
    searchSignalReporter,
  });
  const state = {
    authIntent: null,
    boostBusyListingId: '',
    boostMessage: '',
    busyLabel: '',
    buyerFeedCountry: null,
    buyerFeedPromise: null,
    buyerListingPromise: null,
    currentListingSlug: '',
    currentThreadId: '',
    draftResetSerial: 0,
    draft: draftStorage.loadDraft(),
    inboxError: '',
    inboxItems: [],
    inboxPromise: null,
    inboxStatus: 'idle',
    legalAcceptBusy: false,
    legalAcceptError: '',
    legalDocuments: [],
    legalDocumentsMarket: '',
    legalDocumentsPromise: null,
    legalDocumentsStatus: 'idle',
    legalStatus: null,
    legalStatusError: '',
    legalStatusPromise: null,
    legalStatusStatus: 'idle',
    listingLifecycleBusyId: '',
    listingLifecycleMessage: '',
    otpBusy: false,
    otpError: '',
    pendingChallenge: authService.getPendingChallenge(),
    phoneBusy: false,
    phoneError: '',
    phoneNumber: authService.getPendingChallenge()?.phoneNumber ?? null,
    profile: null,
    shareMenu: null,
    profileCityBusy: false,
    profileCityInput: '',
    profileCityOptions: [],
    profileCityOptionsPromise: null,
    profileCityOptionsStatus: 'idle',
    profileError: '',
    profileMessage: '',
    identityError: '',
    identityMessage: '',
    identitySaveBusy: false,
    profilePromise: null,
    profileSaveBusy: false,
    profileSelectedArea: '',
    profileStatus: 'idle',
    publishError: '',
    publishedDraft: null,
    publishOutcome: null,
    publishedListingRoute: '',
    publishedListingUrl: '',
    reviewErrors: [],
    selectedListingImageIndex: 0,
    currentSellerId: '',
    sellerListings: [],
    sellerListingsError: '',
    sellerListingsPromise: null,
    sellerListingsStatus: 'idle',
    sellerPublic: null,
    sellerPublicError: '',
    sellerPublicPromise: null,
    sellerPublicStatus: 'idle',
    session: authService.loadSession(),
    thread: null,
    threadDraftMessage: '',
    threadError: '',
    threadPromise: null,
    threadStatus: 'idle',
    uploadProgress: null,
    wallet: {
      balanceCdf: 0,
      transactions: [],
    },
    walletError: '',
    walletPromise: null,
    walletStatus: 'idle',
  };
  let lastRenderedRouteKey = '';
  let storyImagePollToken = 0;

  if (!window.location.hash) {
    window.location.hash = '#sell';
  }

  function getRoute() {
    return parseAppRoute(window.location.hash || '#sell');
  }

  function syncReviewPriceUi(form) {
    if (!(form instanceof HTMLFormElement) || form.dataset.form !== 'review-draft') {
      return;
    }

    const currencyField = form.querySelector('[name="priceCurrency"]');
    const amountField = form.querySelector('[name="priceAmount"]');
    const preview = form.querySelector('[data-price-preview]');
    const currency = normalizePriceCurrency(currencyField?.value);

    if (amountField instanceof HTMLInputElement) {
      amountField.disabled = !currency;
      amountField.placeholder = getPriceInputPlaceholder(currency);
    }

    if (preview) {
      preview.textContent = formatPricePreview(amountField?.value ?? '', currency);
    }
  }

  function slugifyTitle(value) {
    return String(value || '')
      .normalize('NFD')
      .replaceAll(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, '-')
      .replaceAll(/^-+|-+$/g, '');
  }

  function buildListingUrl(draft) {
    const previewByCategory = {
      electronics: '/annonce/ordinateur-portable-hp-elitebook/',
      fashion: '/annonce/robe-wax-africaine-taille-m/',
      home_garden: '/annonce/canape-3-places-style-contemporain/',
      phones_tablets: '/annonce/samsung-galaxy-a54-neuf-lubumbashi/',
      real_estate: '/annonce/appartement-2-chambres-quartier-industriel/',
      vehicles: '/annonce/toyota-hilux-2019-4x4/',
    };
    const previewUrl = previewByCategory[draft?.details.categoryId];

    if (previewUrl) {
      return previewUrl;
    }

    const slug = slugifyTitle(draft?.details.title || 'annonce-zwibba') || 'annonce-zwibba';

    return `/annonce/${slug}/`;
  }

  function buildBuyerListingRoute(listingSlug) {
    if (!listingSlug) {
      return '';
    }

    return `#listing/${encodeURIComponent(listingSlug)}`;
  }

  const soldReasonChoices = [
    { code: 'sold_on_zwibba', label: 'Vendu sur Zwibba' },
    { code: 'sold_elsewhere', label: 'Vendu ailleurs' },
  ];
  const deleteReasonChoices = [
    { code: 'not_available', label: 'Plus disponible' },
    { code: 'duplicate_or_error', label: 'Doublon ou erreur' },
    { code: 'republish_later', label: 'Je republierai plus tard' },
    { code: 'other', label: 'Autre' },
  ];

  function promptForLifecycleReason({
    choices,
    message,
  }) {
    const answer = window.prompt(
      `${message}\n\n${choices.map((choice, index) => `${index + 1}. ${choice.label}`).join('\n')}`,
    );

    if (answer === null) {
      return null;
    }

    const normalizedAnswer = String(answer || '').trim().toLowerCase();
    const byIndex = Number.parseInt(normalizedAnswer, 10);

    if (Number.isInteger(byIndex) && byIndex >= 1 && byIndex <= choices.length) {
      return choices[byIndex - 1].code;
    }

    const matchedChoice = choices.find((choice) => {
      return (
        choice.code === normalizedAnswer ||
        choice.label.toLowerCase() === normalizedAnswer
      );
    });

    return matchedChoice?.code ?? '';
  }

  function buildLifecycleFeedbackMessage(action, result) {
    switch (action) {
      case 'delete':
        return 'Annonce archivée. Elle reste restaurable pendant 30 jours.';
      case 'pause':
        return 'Annonce mise en pause.';
      case 'mark_sold':
        return result?.soldChannel || 'Annonce marquée comme vendue.';
      case 'restore':
        return 'Annonce restaurée.';
      case 'resume':
        return 'Annonce remise en ligne.';
      case 'relist':
        return 'Annonce remise en vente.';
      default:
        return 'Annonce mise à jour.';
    }
  }

  function persistDraft(nextDraft) {
    state.draft = postFlowController.saveDraft(nextDraft);
    return state.draft;
  }

  function buildReviewDraftValues(form) {
    const formData = new FormData(form);

    return {
      title: String(formData.get('title') ?? '').trim(),
      categoryId: String(formData.get('categoryId') ?? '').trim(),
      condition: String(formData.get('condition') ?? '').trim(),
      fashionItemType: String(formData.get('fashionItemType') ?? '').trim(),
      fashionSize: String(formData.get('fashionSize') ?? '').trim(),
      priceAmount: formData.get('priceAmount'),
      priceCurrency: String(formData.get('priceCurrency') ?? '').trim(),
      description: String(formData.get('description') ?? '').trim(),
    };
  }

  function syncDraftFromReviewForm(form) {
    if (!(form instanceof HTMLFormElement) || form.dataset.form !== 'review-draft' || !state.draft) {
      return null;
    }

    const reviewDraftValues = buildReviewDraftValues(form);
    const nextDetails = buildReviewDraftDetails({
      existingDetails: state.draft.details,
      profileArea: String(state.profile?.area ?? state.draft.details.area ?? '').trim(),
      values: reviewDraftValues,
    });
    const nextDraft = updateListingDraft(
      state.draft,
      {
        details: nextDetails,
        guidance: getCategoryGuidance(nextDetails.categoryId),
      },
    );

    const persistedDraft = persistDraft(nextDraft);
    state.reviewErrors = refreshReviewValidationErrors(state.reviewErrors, persistedDraft, {
      uploadsBusy: photoUploadQueue.isBusy(),
    });

    return persistedDraft;
  }

  function beginAuthIntent(intent) {
    state.authIntent = intent;
    state.phoneError = '';
    state.otpError = '';
    window.location.hash = '#auth-welcome';
  }

  function resolveAuthContext() {
    switch (state.authIntent?.type) {
      case 'message':
      case 'messages':
        return 'messages';
      case 'wallet':
        return 'wallet';
      case 'profile':
        return 'profile';
      case 'publish':
      default:
        return 'publish';
    }
  }

  function resolveAuthBackHref() {
    return state.authIntent?.returnRoute || '#sell';
  }

  function getActiveTab(route) {
    switch (route.type) {
      case 'buy':
      case 'listing':
      case 'seller':
        return 'buy';
      case 'messages':
      case 'thread':
        return 'messages';
      case 'wallet':
        return 'wallet';
      case 'profile':
        return 'profile';
      default:
        return 'sell';
    }
  }

  function getTotalUnreadMessages() {
    return state.inboxItems.reduce((total, item) => {
      const unreadCount = Number(item?.unreadCount ?? 0);
      return total + (Number.isFinite(unreadCount) ? Math.max(0, unreadCount) : 0);
    }, 0);
  }

  function resolvePostCaptureContinueRoute(draft) {
    return getMissingRequiredPhotoPrompts(draft).length > 0 ? '#guidance' : '#review';
  }

  function resolvePostCaptureContinueLabel(draft) {
    return resolvePostCaptureContinueRoute(draft) === '#guidance'
      ? 'Continuer vers les photos guidées'
      : 'Continuer vers le brouillon';
  }

  function resolveRenderableRoute() {
    const route = getRoute();

    if (route.type === 'listing') {
      return route;
    }

    if (route.type === 'thread' && !state.session) {
      return {
        type: 'messages',
      };
    }

    const draftlessRoute = resolveDraftlessSellerRoute({
      routeType: route.type,
      publishedDraft: state.publishedDraft,
      publishOutcome: state.publishOutcome,
    });

    if (
      !state.draft &&
      draftlessRoute !== route.type
    ) {
      return {
        type: draftlessRoute,
      };
    }

    if (route.type === 'otp' && !state.pendingChallenge) {
      return {
        type: 'phone',
      };
    }

    return route;
  }

  function resolveBrowseCountry() {
    if (state.session) {
      return resolvePhoneCountry(state.session.phoneNumber);
    }

    return countryPreference.getStoredCountry() ?? 'CD';
  }

  function resolveSupportWhatsAppLink() {
    if (typeof window === 'undefined') {
      return null;
    }

    return buildWhatsAppChatLink((window.ZWIBBA_SUPPORT_WHATSAPP ?? {})[resolveBrowseCountry()]);
  }

  async function loadBuyerFeed() {
    const countryCode = resolveBrowseCountry();

    if (state.buyerFeedPromise) {
      if (state.buyerFeedCountry === countryCode) {
        return state.buyerFeedPromise;
      }

      return state.buyerFeedPromise.then(() => loadBuyerFeed());
    }

    state.buyerFeedCountry = countryCode;
    state.buyerFeedPromise = buyerBrowseController
      .loadFeed({
        countryCode,
      })
      .catch(() => undefined)
      .finally(() => {
        state.buyerFeedPromise = null;
        renderApp();
      });

    return state.buyerFeedPromise;
  }

  async function loadBuyerListing(slug) {
    if (!slug) {
      return null;
    }

    if (state.buyerListingPromise && state.currentListingSlug === slug) {
      return state.buyerListingPromise;
    }

    if (state.currentListingSlug !== slug) {
      state.selectedListingImageIndex = 0;
    }

    state.currentListingSlug = slug;
    state.buyerListingPromise = buyerBrowseController
      .loadListing(slug, {
        session: state.session,
      })
      .finally(() => {
        state.buyerListingPromise = null;
        renderApp();
      });

    return state.buyerListingPromise;
  }

  async function loadPublicSeller(sellerId) {
    if (!sellerId) {
      return null;
    }

    if (state.sellerPublicPromise && state.currentSellerId === sellerId) {
      return state.sellerPublicPromise;
    }

    if (state.currentSellerId !== sellerId) {
      state.sellerPublic = null;
      state.sellerPublicError = '';
      state.sellerPublicStatus = 'idle';
    }

    state.currentSellerId = sellerId;
    state.sellerPublicStatus = 'loading';
    state.sellerPublicPromise = profileService
      .fetchPublicSeller({
        sellerId,
      })
      .then((payload) => {
        state.sellerPublic = payload;
        state.sellerPublicStatus = 'ready';
        return payload;
      })
      .catch((error) => {
        state.sellerPublic = null;
        state.sellerPublicStatus = 'error';
        state.sellerPublicError =
          error instanceof Error ? error.message : 'Impossible de charger ce vendeur.';
        return null;
      })
      .finally(() => {
        state.sellerPublicPromise = null;
        renderApp();
      });

    return state.sellerPublicPromise;
  }

  async function loadInbox() {
    if (!state.session) {
      return null;
    }

    if (state.inboxPromise) {
      return state.inboxPromise;
    }

    state.inboxStatus = 'loading';
    state.inboxError = '';
    state.inboxPromise = chatService
      .fetchInbox({
        session: state.session,
      })
      .then((payload) => {
        state.inboxItems = payload.items ?? [];
        state.inboxStatus = 'ready';
        return state.inboxItems;
      })
      .catch((error) => {
        state.inboxItems = [];
        state.inboxStatus = 'error';
        state.inboxError =
          error instanceof Error ? error.message : 'Impossible de charger vos messages.';
        return [];
      })
      .finally(() => {
        state.inboxPromise = null;
        renderApp();
      });

    return state.inboxPromise;
  }

  async function refreshUnreadMessages() {
    if (!state.session || state.inboxPromise) {
      return state.inboxItems;
    }

    const previousUnreadCount = getTotalUnreadMessages();

    try {
      const payload = await chatService.fetchInbox({
        session: state.session,
      });

      state.inboxItems = payload.items ?? [];
      state.inboxStatus = 'ready';
      state.inboxError = '';

      const nextUnreadCount = getTotalUnreadMessages();

      if (nextUnreadCount !== previousUnreadCount) {
        syncUnreadMessagesBadge(appRoot, nextUnreadCount);
      }
    } catch {
      return state.inboxItems;
    }

    return state.inboxItems;
  }

  async function loadThread(threadId) {
    if (!state.session || !threadId) {
      return null;
    }

    if (state.threadPromise && state.currentThreadId === threadId) {
      return state.threadPromise;
    }

    state.currentThreadId = threadId;
    state.threadStatus = 'loading';
    state.threadError = '';
    state.threadPromise = chatService
      .fetchThread({
        session: state.session,
        threadId,
      })
      .then((thread) => {
        state.thread = thread;
        state.threadStatus = 'ready';
        state.inboxItems = state.inboxItems.map((item) =>
          item.id === thread.id
            ? {
                ...item,
                lastMessagePreview: thread.messages?.at(-1)?.body ?? item.lastMessagePreview,
                unreadCount: 0,
              }
            : item,
        );
        return thread;
      })
      .catch((error) => {
        state.thread = null;
        state.threadStatus = 'error';
        state.threadError =
          error instanceof Error ? error.message : 'Impossible de charger cette conversation.';
        return null;
      })
      .finally(() => {
        state.threadPromise = null;
        renderApp();
      });

    return state.threadPromise;
  }

  async function loadWallet() {
    if (!state.session) {
      return null;
    }

    if (state.walletPromise) {
      return state.walletPromise;
    }

    state.walletStatus = 'loading';
    state.walletError = '';
    state.walletPromise = walletService
      .fetchWallet({
        session: state.session,
      })
      .then((wallet) => {
        state.wallet = wallet;
        state.walletStatus = 'ready';
        return wallet;
      })
      .catch((error) => {
        state.wallet = {
          balanceCdf: 0,
          transactions: [],
        };
        state.walletStatus = 'error';
        state.walletError =
          error instanceof Error ? error.message : 'Impossible de charger le portefeuille.';
        return state.wallet;
      })
      .finally(() => {
        state.walletPromise = null;
        renderApp();
      });

    return state.walletPromise;
  }

  async function loadSellerListings() {
    if (!state.session) {
      return null;
    }

    if (state.sellerListingsPromise) {
      return state.sellerListingsPromise;
    }

    state.sellerListingsStatus = 'loading';
    state.sellerListingsError = '';
    state.sellerListingsPromise = sellerListingsService
      .listMine({
        session: state.session,
      })
      .then((payload) => {
        state.sellerListings = payload.items ?? [];
        state.sellerListingsError = '';
        state.sellerListingsStatus = 'ready';
        return state.sellerListings;
      })
      .catch((error) => {
        state.sellerListings = [];
        state.sellerListingsStatus = 'error';
        state.sellerListingsError =
          error instanceof Error ? error.message : 'Impossible de charger vos annonces.';
        return [];
      })
      .finally(() => {
        state.sellerListingsPromise = null;
        renderApp();
      });

    return state.sellerListingsPromise;
  }

  async function loadProfile() {
    if (!state.session) {
      return null;
    }

    if (state.profilePromise) {
      return state.profilePromise;
    }

    state.profileStatus = 'loading';
    state.profileError = '';
    const profileCityInputAtLoadStart = state.profileCityInput;
    state.profilePromise = profileService
      .fetchProfile({
        session: state.session,
      })
      .then((profile) => {
        const hydratedCity = resolveProfileCityHydration({
          currentInput: state.profileCityInput,
          currentSelectedArea: state.profileSelectedArea,
          inputAtLoadStart: profileCityInputAtLoadStart,
          profileArea: profile.area ?? '',
        });

        state.profile = profile;
        state.profileCityInput = hydratedCity.inputValue;
        state.profileSelectedArea = hydratedCity.selectedArea;
        state.profileStatus = 'ready';
        state.draft = syncDraftAreaFromProfile(state.draft, profile.area);
        if (state.draft) {
          draftStorage.saveDraft(state.draft);
        }
        return profile;
      })
      .catch((error) => {
        state.profile = null;
        state.profileStatus = 'error';
        state.profileError =
          error instanceof Error ? error.message : 'Impossible de charger votre profil.';
        return null;
      })
      .finally(() => {
        state.profilePromise = null;
        renderApp();
      });

    return state.profilePromise;
  }

  async function loadProfileCityOptions() {
    if (state.profileCityOptionsPromise) {
      return state.profileCityOptionsPromise;
    }

    state.profileCityOptionsStatus = 'loading';
    state.profileCityOptionsPromise = profileService
      .listCities({
        countryCode: resolvePhoneCountry(state.session?.phoneNumber),
      })
      .then((items) => {
        state.profileCityOptions = items;
        state.profileCityOptionsStatus = 'ready';
        return items;
      })
      .catch((error) => {
        state.profileCityOptions = [];
        state.profileCityOptionsStatus = 'error';
        state.profileError =
          error instanceof Error ? error.message : 'Impossible de charger les villes.';
        return [];
      })
      .finally(() => {
        state.profileCityOptionsPromise = null;
        renderApp();
      });

    return state.profileCityOptionsPromise;
  }

  const accountRouteTypes = new Set(['messages', 'thread', 'wallet', 'profile', 'publish']);

  // Only a status confirmed by the server gates the account screens. A status
  // that could not be read is reported as such, never as "nothing to accept".
  function requiresTermsAcceptance() {
    return Boolean(
      state.session && state.legalStatus?.active && state.legalStatus?.needsAcceptance,
    );
  }

  function loadLegalStatus() {
    const sessionToken = state.session?.sessionToken;

    if (!sessionToken) {
      return Promise.resolve(null);
    }

    if (state.legalStatusPromise) {
      return state.legalStatusPromise;
    }

    state.legalStatusStatus = 'loading';
    state.legalStatusError = '';
    state.legalStatusPromise = authService
      .getLegalStatus({
        sessionToken,
      })
      .then((legalStatus) => {
        // A logout or a new session during the request must not adopt this answer.
        if (state.session?.sessionToken !== sessionToken) {
          return null;
        }

        state.legalStatus = legalStatus;
        state.legalStatusStatus = 'ready';
        return legalStatus;
      })
      .catch((error) => {
        if (state.session?.sessionToken !== sessionToken) {
          return null;
        }

        state.legalStatusStatus = 'error';
        state.legalStatusError =
          error instanceof Error
            ? error.message
            : 'Statut des conditions générales indisponible.';
        return null;
      })
      .finally(() => {
        if (state.session?.sessionToken === sessionToken) state.legalStatusPromise = null;
        renderApp();
      });

    return state.legalStatusPromise;
  }

  function loadLegalDocuments() {
    if (state.legalDocumentsPromise) {
      return state.legalDocumentsPromise;
    }

    const market = resolveBrowseCountry();

    state.legalDocumentsStatus = 'loading';
    state.legalDocumentsMarket = market;
    state.legalDocumentsPromise = authService
      .getLegalDocuments({
        market,
      })
      .then((catalog) => {
        // A draft catalog publishes nothing: no link is invented for it.
        state.legalDocuments = catalog?.active ? catalog.documents : [];
        state.legalDocumentsStatus = 'ready';
        return catalog;
      })
      .catch(() => {
        state.legalDocuments = [];
        state.legalDocumentsStatus = 'error';
        return null;
      })
      .finally(() => {
        state.legalDocumentsPromise = null;
        renderApp();
      });

    return state.legalDocumentsPromise;
  }

  function primeBuyerRouteState(route) {
    const enteredRoute = lastRenderedRouteKey !== getRenderableRouteKey(route);
    if (state.session && state.legalStatusStatus === 'idle') {
      void loadLegalStatus();
    }

    if (
      (route.type === 'auth-welcome' || route.type === 'phone' || route.type === 'otp') &&
      (state.legalDocumentsStatus === 'idle' ||
        state.legalDocumentsMarket !== resolveBrowseCountry())
    ) {
      void loadLegalDocuments();
    }

    // Account data is pointless while the server refuses the session over terms.
    // Public browsing keeps loading normally.
    const blockedByTerms = requiresTermsAcceptance();

    if (
      state.session &&
      !blockedByTerms &&
      (
        state.profileStatus === 'idle' ||
        (route.type === 'seller' && enteredRoute && state.profileStatus === 'error')
      )
    ) {
      void loadProfile();
    }

    if ((route.type === 'buy' || route.type === 'sell') && buyerBrowseController.state.feedStatus === 'idle') {
      void loadBuyerFeed();
    }

    if (
      route.type === 'listing' &&
      (state.currentListingSlug !== route.slug ||
        buyerBrowseController.state.detailStatus === 'idle' ||
        (enteredRoute && buyerBrowseController.state.detailStatus === 'error'))
    ) {
      void loadBuyerListing(route.slug);
    }

    if (
      route.type === 'seller' &&
      (state.currentSellerId !== route.sellerId ||
        state.sellerPublicStatus === 'idle' ||
        (enteredRoute && state.sellerPublicStatus === 'error'))
    ) {
      void loadPublicSeller(route.sellerId);
    }

    if (route.type === 'messages' && state.session && !blockedByTerms && state.inboxStatus === 'idle') {
      void loadInbox();
    }

    if (route.type === 'thread' && state.session && !blockedByTerms) {
      if (state.currentThreadId !== route.threadId || state.threadStatus === 'idle' ||
        (enteredRoute && state.threadStatus === 'error')) {
        void loadThread(route.threadId);
      }
    }

    if (route.type === 'wallet' && state.session && !blockedByTerms && state.walletStatus === 'idle') {
      void loadWallet();
    }

    if (route.type === 'profile' && state.session && !blockedByTerms && state.sellerListingsStatus === 'idle') {
      void loadSellerListings();
    }

    if (route.type === 'profile' && state.profileCityOptionsStatus === 'idle') {
      void loadProfileCityOptions();
    }
  }

  function renderRoute(route) {
    if (accountRouteTypes.has(route.type) && requiresTermsAcceptance()) {
      return renderTermsAcceptanceScreen({
        busy: state.legalAcceptBusy || state.legalStatusStatus === 'loading',
        errorMessage: state.legalAcceptError,
        legal: state.legalStatus,
        statusErrorMessage: state.legalStatusError,
      });
    }

    // An unreadable status is surfaced where it matters, without blocking the
    // account screens: the API stays authoritative and answers 428 if needed.
    const legalStatusNotice = accountRouteTypes.has(route.type)
      ? renderLegalStatusNotice({
          busy: state.legalStatusStatus === 'loading',
          errorMessage: state.legalStatusError,
        })
      : '';
    // Published documents stay readable while browsing without an account.
    const publicLegalLinks =
      route.type === 'auth-welcome' || route.type === 'phone'
        ? renderLegalLinks({
            documents: state.legalDocuments,
          })
        : '';

    return legalStatusNotice + renderRouteContent(route) + publicLegalLinks;
  }

  function renderRouteContent(route) {
    const homeSections = buyerBrowseController.getHomeSections();
    const homeFeedStatus =
      buyerBrowseController.state.feedStatus === 'idle'
        ? 'loading'
        : buyerBrowseController.state.feedStatus;

    switch (route.type) {
      case 'capture':
        return renderCaptureScreen({
          busyLabel: state.busyLabel,
          draft: state.draft,
          uploadProgress: state.uploadProgress,
        });
      case 'capture-result':
        return renderCaptureResultScreen({
          continueHref: resolvePostCaptureContinueRoute(state.draft),
          continueLabel: resolvePostCaptureContinueLabel(state.draft),
          draft: state.draft,
        });
      case 'guidance':
        return renderPhotoGuidanceScreen({
          draft: state.draft,
          uploadProgress: state.uploadProgress,
          uploadsBusy: photoUploadQueue.isBusy(),
        });
      case 'review':
        return renderReviewFormScreen({
          categories: sellerCategories,
          conditionOptions,
          draft: state.draft,
          profileArea: state.profile?.area ?? state.draft?.details.area ?? '',
          sellerCountryCode: resolvePhoneCountry(state.session?.phoneNumber),
          validationErrors: state.reviewErrors,
        });
      case 'auth-welcome':
        return renderAuthWelcomeScreen({
          backHref: resolveAuthBackHref(),
          context: resolveAuthContext(),
        });
      case 'phone':
        return renderPhoneInputScreen({
          busy: state.phoneBusy,
          errorMessage: state.phoneError,
          phoneNumber:
            state.phoneNumber ?? resolveDefaultPhonePrefix(resolveBrowseCountry()),
        });
      case 'otp':
        return renderOtpScreen({
          busy: state.otpBusy,
          // Only a demo challenge carries a code to display.
          demoCode: state.pendingChallenge?.demoCode ?? '',
          errorMessage: state.otpError,
          legal: state.pendingChallenge?.legal ?? null,
          phoneNumber: state.pendingChallenge?.phoneNumber ?? state.phoneNumber,
        });
      case 'publish':
        return renderPublishGateScreen({
          busyLabel: state.busyLabel,
          draft: state.draft,
          errorMessage: state.publishError,
          session: state.session,
        });
      case 'success':
        {
          const successDraft = state.publishedDraft ?? state.draft ?? {
            details: {
              area: '',
              priceAmount: 0,
              priceCurrency: 'CDF',
              title: '',
            },
            photos: [],
          };

        return renderSuccessScreen({
          boostBusy: state.boostBusyListingId === state.publishOutcome?.id,
          boostMessage: state.boostMessage,
          draft: successDraft,
          listingRoute: state.publishedListingRoute,
          listingUrl:
            state.publishedListingUrl || buildListingUrl(successDraft),
          outcome: state.publishOutcome,
        });
        }
      case 'listing':
        return renderListingDetailScreen({
          detail: buyerBrowseController.state.detail,
          errorMessage: buyerBrowseController.state.detailError,
          contactBusy: threadStarter.state.busy,
          contactError: threadStarter.state.listingId === buyerBrowseController.state.detail?.id ? threadStarter.state.error : '',
          selectedImageIndex: state.selectedListingImageIndex,
          state: buyerBrowseController.state.detailStatus,
        });
      case 'seller':
        return renderSellerPublicScreen({
          isOwner: Boolean(
            state.session &&
              state.profile &&
              state.profile.id &&
              state.sellerPublic &&
              state.sellerPublic.seller &&
              state.profile.id === state.sellerPublic.seller.id,
          ),
          listings: state.sellerPublic?.listings ?? [],
          reviews: state.sellerPublic?.reviews ?? [],
          seller: state.sellerPublic?.seller ?? null,
          state: state.sellerPublicStatus,
          viewerUserId: state.profile?.id ?? null,
        });
      case 'messages':
        return renderInboxScreen({
          items: state.inboxItems,
          state: state.session
            ? state.inboxStatus === 'idle'
              ? 'loading'
              : state.inboxStatus === 'error'
                ? 'ready'
                : state.inboxStatus
            : 'locked',
        });
      case 'thread':
        return state.session
          ? renderThreadScreen({
              draftMessage: state.threadDraftMessage,
              isSending: state.threadStatus === 'sending',
              thread: state.threadStatus === 'error' ? null : state.thread,
            })
          : renderInboxScreen({
              state: 'locked',
            });
      case 'wallet':
        return renderWalletScreen({
          state: state.session
            ? state.walletStatus === 'idle'
              ? 'loading'
              : state.walletStatus
            : 'locked',
          wallet: state.wallet,
        });
      case 'profile':
        {
          const profileCityState = deriveProfileCityAutocompleteState({
            cityOptions: state.profileCityOptions,
            inputValue: state.profileCityInput,
            selectedArea: state.profileSelectedArea,
          });

        return renderProfileScreen({
          citySuggestions: profileCityState.suggestions,
          draftExists: Boolean(state.draft),
          lifecycleMessage: state.listingLifecycleMessage,
          listings: state.sellerListings,
          listingsError: state.sellerListingsError,
          profile: state.profile,
          profileAreaInput: profileCityState.inputValue,
          profileCityBusy: state.profileCityBusy,
          session: state.session,
          profileError: state.profileError,
          identityError: state.identityError,
          identityMessage: state.identityMessage,
          identitySaveBusy: state.identitySaveBusy,
          profileMissingCityLabel: profileCityState.missingCityLabel,
          profileMessage: state.profileMessage,
          profileSaveBusy: state.profileSaveBusy,
          selectedProfileArea: profileCityState.selectedArea,
          profileState: state.profileStatus,
          supportWhatsAppLink: resolveSupportWhatsAppLink(),
          state: state.session
            ? state.sellerListingsStatus === 'idle'
              ? 'loading'
              : state.sellerListingsStatus
            : 'locked',
        });
        }
      case 'buy':
        return renderBuyScreen({
          activeCountry: resolveBrowseCountry(),
          categories: sellerCategories,
          featuredListings: homeSections.featuredListings,
          feedStatus: homeFeedStatus,
          hasSession: Boolean(state.session),
          recentListings: homeSections.recentListings,
          searchQuery: buyerBrowseController.state.searchQuery,
          selectedCategoryId: buyerBrowseController.state.selectedCategoryId,
        });
      case 'sell':
      default:
        return renderHomeScreen({
          draft: state.draft,
          categories: sellerCategories,
          featuredListings: homeSections.featuredListings,
          feedStatus: homeFeedStatus,
          recentListings: homeSections.recentListings,
          searchQuery: buyerBrowseController.state.searchQuery,
          selectedCategoryId: buyerBrowseController.state.selectedCategoryId,
        });
    }
  }

  function updateProfileCityField(profileCityState, { focusInput = false, inputValue = null } = {}) {
    const form = appRoot.querySelector('form[data-form="profile-zone"]');

    if (!form) {
      return false;
    }

    const hiddenAreaInput = form.querySelector('input[name="area"]');
    const searchInput = form.querySelector('input[name="areaSearch"]');
    const feedback = form.querySelector('[data-profile-city-feedback]');

    if (hiddenAreaInput instanceof HTMLInputElement) {
      hiddenAreaInput.value = profileCityState.selectedArea;
      hiddenAreaInput.dataset.selectedArea = profileCityState.selectedArea;
    }

    if (searchInput instanceof HTMLInputElement) {
      if (inputValue !== null) {
        searchInput.value = inputValue;
      }

      searchInput.setAttribute(
        'aria-expanded',
        profileCityState.suggestions.length > 0 || profileCityState.missingCityLabel
          ? 'true'
          : 'false',
      );

      if (focusInput) {
        searchInput.focus();
        searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
      }
    }

    if (feedback) {
      feedback.innerHTML = renderProfileCityFeedback({
        citySuggestions: profileCityState.suggestions,
        profileMissingCityLabel: profileCityState.missingCityLabel,
      });
    }

    form.querySelector('[data-profile-zone-error]')?.remove();
    form.querySelector('[data-profile-zone-message]')?.remove();

    return true;
  }

  const legalFormFieldNames = new Set(['acceptedTerms', 'otpCode']);

  function captureLegalFormRenderState(root, activeElement) {
    const form = root?.querySelector(
      'form[data-form="verify-otp"], form[data-form="accept-terms"]',
    );

    if (!form) {
      return null;
    }

    const codeInput = form.querySelector('input[name="otpCode"]');
    const consentInput = form.querySelector('input[name="acceptedTerms"]');

    return {
      accepted: consentInput ? consentInput.checked === true : false,
      code: codeInput ? String(codeInput.value ?? '') : '',
      focusedName:
        activeElement && legalFormFieldNames.has(activeElement.name) ? activeElement.name : '',
      formName: form.dataset.form,
      termsHash: form.dataset.termsHash ?? '',
      termsLocale: form.dataset.termsLocale ?? '',
      termsVersion: form.dataset.termsVersion ?? '',
    };
  }

  function restoreLegalFormRenderState(root, capturedState) {
    if (!root || !capturedState) {
      return;
    }

    const form = root.querySelector(`form[data-form="${capturedState.formName}"]`);

    if (!form) {
      return;
    }

    const codeInput = form.querySelector('input[name="otpCode"]');

    if (codeInput instanceof HTMLInputElement && capturedState.code) {
      codeInput.value = capturedState.code;
    }

    const consentInput = form.querySelector('input[name="acceptedTerms"]');

    if (consentInput instanceof HTMLInputElement) {
      // Acceptance always targets the exact text displayed: a new version, hash
      // or locale clears the tick and asks for an explicit consent again.
      const sameTerms =
        (form.dataset.termsVersion ?? '') === capturedState.termsVersion &&
        (form.dataset.termsHash ?? '') === capturedState.termsHash &&
        (form.dataset.termsLocale ?? '') === capturedState.termsLocale;

      consentInput.checked = sameTerms ? capturedState.accepted : false;
    }

    if (capturedState.focusedName) {
      const focusTarget = form.querySelector(`[name="${capturedState.focusedName}"]`);

      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus();
      }
    }
  }

  function shouldShowCountrySuggestion() {
    return (
      !state.session &&
      !countryPreference.getStoredCountry() &&
      readGeoCountry(typeof document === 'undefined' ? '' : document.cookie) === 'BE'
    );
  }

  function renderApp() {
    const previousShareAction = document.activeElement?.closest('.app-share-menu') ? document.activeElement.dataset.action : null;
    const route = resolveRenderableRoute();
    const routeKey = getRenderableRouteKey(route);
    const previousMarketMenu = appRoot.querySelector('[data-market-menu]');
    const marketMenuState = lastRenderedRouteKey === routeKey && previousMarketMenu ? {
      country: previousMarketMenu.dataset.country,
      open: previousMarketMenu.open,
      focused: previousMarketMenu.contains(document.activeElement),
      focusCountry: document.activeElement?.dataset.country,
    } : null;
    const scrollRenderState =
      pendingScrollReset.consume(route.type) ??
      (lastRenderedRouteKey === routeKey ? captureScrollRenderState(appRoot, window) : null);
    const buyerCategoryScrollRenderState = captureBuyerCategoryScrollRenderState(appRoot);
    const buyerSearchRenderState = captureBuyerSearchRenderState(document.activeElement);
    const reviewDraftRenderState = captureReviewDraftRenderState(appRoot, document.activeElement);
    const threadComposerRenderState = captureThreadComposerRenderState(document.activeElement);
    const profileCityRenderState = captureProfileCityRenderState(document.activeElement);
    const legalFormRenderState = captureLegalFormRenderState(appRoot, document.activeElement);

    window.ZWIBBA_ACTIVE_COUNTRY_CODE = resolveBrowseCountry();

    primeBuyerRouteState(route);
    chatLiveRefreshController.sync({
      refreshInbox: loadInbox,
      refreshThread: loadThread,
      refreshUnreadMessages,
      route,
      session: state.session,
    });
    appRoot.innerHTML =
      renderAppTabShell({
        activeTab: getActiveTab(route),
        content: renderRoute(route),
        unreadMessagesCount: getTotalUnreadMessages(),
      }) +
      renderShareMenu(state.shareMenu) +
      (shouldShowCountrySuggestion() ? renderCountrySuggestionBanner() : '');
    const nextMarketMenu = appRoot.querySelector('[data-market-menu]');
    if (nextMarketMenu && marketMenuState) {
      nextMarketMenu.open = marketMenuState.open && nextMarketMenu.dataset.country === marketMenuState.country;
      if (marketMenuState.focused) {
        const option = nextMarketMenu.open && [...nextMarketMenu.querySelectorAll('button')]
          .find(button => button.dataset.country === marketMenuState.focusCountry);
        (option || nextMarketMenu.querySelector('summary'))?.focus({ preventScroll: true });
      }
    }
    if (route.type === 'buy') {
      restoreBuyerSearchRenderState(appRoot, buyerSearchRenderState);
    }
    if (route.type === 'buy' || route.type === 'sell') {
      restoreBuyerCategoryScrollRenderState(appRoot, buyerCategoryScrollRenderState);
    }
    if (route.type === 'review') {
      restoreReviewDraftRenderState(appRoot, reviewDraftRenderState);
      const reviewForm = appRoot.querySelector('form[data-form="review-draft"]');

      if (reviewForm instanceof HTMLFormElement) {
        syncReviewPriceUi(reviewForm);
      }
    }
    if (route.type === 'thread') {
      restoreThreadComposerRenderState(appRoot, threadComposerRenderState);
    }
    if (route.type === 'profile') {
      restoreProfileCityRenderState(appRoot, profileCityRenderState);
    }
    restoreLegalFormRenderState(appRoot, legalFormRenderState);
    restoreScrollRenderState(appRoot, scrollRenderState, window);
    if (state.shareMenu) syncShareFocus(previousShareAction);
    appRoot.dataset.appReady = 'true';
    appRoot.dataset.screen = route.type;
    lastRenderedRouteKey = routeKey;
  }

  async function handleCapture(file, draftResetSerial = state.draftResetSerial) {
    state.busyLabel = 'Compression, téléversement et analyse IA en cours...';
    renderApp();

    try {
      const nextDraft = await postFlowController.captureFirstPhoto(file);

      if (draftResetSerial !== state.draftResetSerial) {
        if (!state.draft) {
          draftStorage.clearDraft();
        }
        state.busyLabel = '';
        renderApp();
        return;
      }

      state.busyLabel = '';
      state.uploadProgress = null;
      state.draft = syncDraftAreaFromProfile(nextDraft, state.profile?.area ?? '');
      if (state.draft) {
        draftStorage.saveDraft(state.draft);
      }
      state.publishError = '';
      state.publishedDraft = null;
      state.publishOutcome = null;
      state.publishedListingRoute = '';
      state.publishedListingUrl = '';
      state.reviewErrors = [];
      window.location.hash = '#capture-result';
    } catch (error) {
      if (draftResetSerial !== state.draftResetSerial) {
        if (!state.draft) {
          draftStorage.clearDraft();
        }
        state.busyLabel = '';
        renderApp();
        return;
      }

      state.busyLabel = '';
      state.uploadProgress = null;
      state.draft = syncDraftAreaFromProfile(error?.draft ?? state.draft, state.profile?.area ?? '');
      if (state.draft) {
        draftStorage.saveDraft(state.draft);
      }
      renderApp();
    }
  }

  async function handlePrimaryPhotoReplacement(file, draftResetSerial = state.draftResetSerial) {
    state.busyLabel = 'Remplacement de la photo et analyse IA en cours...';
    renderApp();

    try {
      const nextDraft = await postFlowController.replacePrimaryPhoto(file);

      if (draftResetSerial !== state.draftResetSerial) {
        state.busyLabel = '';
        renderApp();
        return;
      }

      state.busyLabel = '';
      state.uploadProgress = null;
      state.draft = syncDraftAreaFromProfile(nextDraft, state.profile?.area ?? '');
      if (state.draft) {
        draftStorage.saveDraft(state.draft);
      }
      state.publishError = '';
      state.reviewErrors = [];
      window.location.hash = '#capture-result';
    } catch (error) {
      if (draftResetSerial !== state.draftResetSerial) {
        state.busyLabel = '';
        renderApp();
        return;
      }

      state.busyLabel = '';
      state.uploadProgress = null;
      state.draft = syncDraftAreaFromProfile(error?.draft ?? state.draft, state.profile?.area ?? '');
      state.publishError =
        error instanceof Error ? error.message : 'Impossible de remplacer la photo principale.';
      renderApp();
    }
  }

  async function handleGuidedCapture(promptId, file, draftResetSerial = state.draftResetSerial) {
    state.busyLabel = 'Compression et téléversement de la photo guidée...';
    renderApp();

    try {
      const nextDraft = await postFlowController.addGuidedPhoto(promptId, file);

      if (draftResetSerial !== state.draftResetSerial) {
        if (!state.draft) {
          draftStorage.clearDraft();
        }
        state.busyLabel = '';
        renderApp();
        return;
      }

      state.draft = syncDraftAreaFromProfile(nextDraft, state.profile?.area ?? '');
      if (state.draft) {
        draftStorage.saveDraft(state.draft);
      }
      state.publishError = '';
      state.publishedDraft = null;
      state.reviewErrors = [];
      state.busyLabel = '';
      state.uploadProgress = null;
      renderApp();
    } catch (error) {
      if (draftResetSerial !== state.draftResetSerial) {
        if (!state.draft) {
          draftStorage.clearDraft();
        }
        state.busyLabel = '';
        renderApp();
        return;
      }

      state.busyLabel = '';
      state.uploadProgress = null;
      state.draft = syncDraftAreaFromProfile(error?.draft ?? state.draft, state.profile?.area ?? '');
      if (state.draft) {
        draftStorage.saveDraft(state.draft);
      }
      renderApp();
    }
  }

  async function handleDiscardDraft() {
    if (!state.draft) {
      return;
    }

    const confirmed = window.confirm(
      'Supprimer ce brouillon ?\n\nLes photos déjà téléversées et les informations saisies seront supprimées.',
    );

    if (!confirmed) {
      return;
    }

    const draftToDiscard = state.draft;

    state.busyLabel = 'Suppression du brouillon...';
    state.draftResetSerial += 1;
    photoUploadQueue.cancelAll();
    renderApp();

    try {
      if (draftToDiscard.remoteDraftId) {
        if (!state.session) {
          throw new Error('Connectez-vous avec le numéro du brouillon pour le supprimer.');
        }

        await liveDraftService.deleteDraft({
          draftId: draftToDiscard.remoteDraftId,
          session: state.session,
        });
      } else {
        const uploadedObjectKeys = draftToDiscard.photos
          .map((photo) => photo.objectKey || '')
          .filter(Boolean);

        if (uploadedObjectKeys.length && state.session?.sessionToken) {
          await mediaService.deleteUploadedObjects({
            objectKeys: uploadedObjectKeys,
            session: state.session,
          });
        }
      }

      draftStorage.clearDraft();
      state.busyLabel = '';
      state.draft = null;
      state.publishError = '';
      state.publishedDraft = null;
      state.publishOutcome = null;
      state.publishedListingRoute = '';
      state.publishedListingUrl = '';
      state.reviewErrors = [];
      const nextDiscardRoute = resolveDiscardDraftRoute(window.location.hash || '#sell');

      if (nextDiscardRoute) {
        window.location.hash = nextDiscardRoute;
      } else {
        renderApp();
      }
    } catch (error) {
      state.busyLabel = '';
      renderApp();
      window.alert(
        error instanceof Error ? error.message : 'Impossible de supprimer le brouillon.',
      );
    }
  }

  async function handleReviewSubmit(form) {
    if (!state.draft) {
      return;
    }

    if (state.session && (!state.profile || state.profileStatus === 'idle')) {
      await loadProfile();
    }

    const nextDraft = syncDraftFromReviewForm(form);

    if (!nextDraft) {
      return;
    }

    state.publishError = '';
    state.publishedDraft = null;
    state.publishOutcome = null;
    state.publishedListingRoute = '';
    state.publishedListingUrl = '';
    state.reviewErrors = validateDraftForPublish(nextDraft, {
      uploadsBusy: photoUploadQueue.isBusy(),
    });

    if (state.reviewErrors.length) {
      renderApp();
      return;
    }

    const publishGate = decidePublishGate({
      draft: nextDraft,
      session: state.session,
      uploadsBusy: photoUploadQueue.isBusy(),
    });

    if (publishGate.nextRoute !== '#publish') {
      state.authIntent = {
        returnRoute: '#review',
        type: 'publish',
      };
    } else {
      state.authIntent = null;
    }

    window.location.hash = publishGate.nextRoute;
  }

  async function handlePhoneSubmit(form) {
    if (state.phoneBusy) return;
    const formData = new FormData(form);
    state.phoneNumber = String(formData.get('phoneNumber') ?? '');
    state.phoneBusy = true;
    state.phoneError = '';
    renderApp();

    try {
      const challenge = await authService.requestOtp({
        phoneNumber: state.phoneNumber,
      });

      state.pendingChallenge = challenge;
      state.phoneNumber = challenge.phoneNumber;
      state.phoneError = '';
      state.otpError = '';
      window.location.hash = '#otp';
    } catch (error) {
      state.phoneError = error instanceof Error ? error.message : 'Numéro invalide.';
    } finally {
      state.phoneBusy = false;
      renderApp();
    }
  }

  const threadStarter = createThreadStarter({ createThread: context => chatService.createThread(context), onChange: () => renderApp() });

  async function openThreadFromListing({
    listingId,
    listingSlug = '',
  }) {
    if (!state.session) {
      return;
    }

    const result = await threadStarter.start({ listingId, session: state.session });
    if (result.status === 'auth-required') {
      authService.clearSession();
      state.session = null;
      beginAuthIntent({ listingId, listingSlug, type: 'message', returnRoute: `#listing/${encodeURIComponent(listingSlug)}` });
    }
    if (result.status === 'terms-required') {
      state.legalStatusStatus = 'idle';
      void loadLegalStatus();
    }
    if (result.status !== 'ready') return;
    const thread = result.thread;

    state.thread = thread;
    state.threadStatus = 'ready';
    state.currentThreadId = thread.id;
    state.threadDraftMessage = '';
    state.inboxStatus = 'idle';
    state.authIntent = null;
    window.location.hash = `#thread/${encodeURIComponent(thread.id)}`;
    void loadInbox();
    if (listingSlug) {
      state.currentListingSlug = listingSlug;
    }
  }

  function handleLogout() {
    authService.clearSession();
    state.session = null;
    state.authIntent = null;
    state.inboxItems = [];
    state.inboxPromise = null;
    state.inboxStatus = 'idle';
    state.thread = null;
    state.threadDraftMessage = '';
    state.threadError = '';
    state.threadPromise = null;
    state.threadStatus = 'idle';
    state.wallet = {
      balanceCdf: 0,
      transactions: [],
    };
    state.walletError = '';
    state.walletPromise = null;
    state.walletStatus = 'idle';
    state.profile = null;
    state.profileCityBusy = false;
    state.profileCityInput = '';
    state.profileCityOptions = [];
    state.profileCityOptionsPromise = null;
    state.profileCityOptionsStatus = 'idle';
    state.profileError = '';
    state.profileMessage = '';
    state.profilePromise = null;
    state.profileSaveBusy = false;
    state.profileSelectedArea = '';
    state.profileStatus = 'idle';
    state.sellerListings = [];
    state.sellerListingsError = '';
    state.sellerListingsPromise = null;
    state.sellerListingsStatus = 'idle';
    state.listingLifecycleBusyId = '';
    state.listingLifecycleMessage = '';
    state.legalAcceptBusy = false;
    state.legalAcceptError = '';
    state.legalStatus = null;
    state.legalStatusError = '';
    state.legalStatusPromise = null;
    state.legalStatusStatus = 'idle';
    buyerBrowseController.state.detail = null;
    buyerBrowseController.state.detailError = '';
    buyerBrowseController.state.detailStatus = 'idle';
    buyerBrowseController.state.feedItems = [];
    buyerBrowseController.state.feedStatus = 'idle';
    window.location.hash = '#auth-welcome';
    renderApp();
  }

  // The checkbox is the only source of a consent: nothing is sent when the user
  // did not tick it.
  function readLegalAcceptance(formData) {
    if (String(formData.get('acceptedTerms') ?? '') !== 'true') {
      return null;
    }

    return {
      accepted: true,
      version: String(formData.get('termsVersion') ?? ''),
      hash: String(formData.get('termsHash') ?? ''),
      locale: String(formData.get('termsLocale') ?? ''),
    };
  }

  async function refreshPendingChallengeLegal() {
    const previousChallenge = state.pendingChallenge;
    try {
      const challenge = await authService.refreshPendingChallengeLegal();
      if (state.pendingChallenge?.challengeId === previousChallenge?.challengeId &&
          state.pendingChallenge?.phoneNumber === previousChallenge?.phoneNumber) {
        state.pendingChallenge = challenge;
      }
    } catch {
      // The server message already explains the refusal; the pending code stays
      // usable and the user can retry.
    }
  }

  async function handleOtpSubmit(form) {
    if (state.otpBusy) return;
    const formData = new FormData(form);
    const legalAcceptance = readLegalAcceptance(formData);

    if (state.pendingChallenge?.legal?.required && !legalAcceptance) {
      state.otpError =
        'Veuillez lire et accepter les conditions générales d’utilisation (CGU) pour continuer.';
      renderApp();
      return;
    }

    state.otpBusy = true;
    state.otpError = '';
    // Update pending controls in place: keep the typed code and explicit consent.
    form.setAttribute('aria-busy', 'true');
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Vérification…';
    }

    try {
      const session = await authService.verifyOtp({
        code: String(formData.get('otpCode') ?? '').trim(),
        phoneNumber: state.pendingChallenge?.phoneNumber ?? state.phoneNumber,
        ...(legalAcceptance ? { legalAcceptance } : {}),
      });

      state.session = session;
      state.pendingChallenge = authService.getPendingChallenge();
      state.otpError = '';
      state.inboxStatus = 'idle';
      state.profile = null;
      state.profileCityBusy = false;
      state.profileCityInput = '';
      state.profileCityOptions = [];
      state.profileCityOptionsPromise = null;
      state.profileCityOptionsStatus = 'idle';
      state.profileError = '';
      state.profileMessage = '';
      state.profileStatus = 'idle';
      state.profileSelectedArea = '';
      state.walletStatus = 'idle';
      state.sellerListingsStatus = 'idle';
      state.legalStatus = null;
      state.legalStatusError = '';
      state.legalStatusStatus = 'idle';
      state.legalAcceptError = '';

      if (state.draft) {
        state.draft = markDraftOtpVerified(state.draft, {
          phoneNumber: session.phoneNumber,
        });
        draftStorage.saveDraft(state.draft);
      }

      void loadProfile();

      if (state.authIntent?.type === 'message' && state.authIntent.listingId) {
        await openThreadFromListing({
          listingId: state.authIntent.listingId,
          listingSlug: state.authIntent.listingSlug,
        });
        return;
      }

      if (state.authIntent?.type === 'messages' || state.authIntent?.type === 'wallet' || state.authIntent?.type === 'profile') {
        const returnRoute = state.authIntent.returnRoute || '#profile';
        state.authIntent = null;
        window.location.hash = returnRoute;
        return;
      }

      state.authIntent = null;
      window.location.hash = '#publish';
    } catch (error) {
      state.otpError = error instanceof Error ? error.message : 'Code OTP invalide.';

      // A version published while the user was typing: reload the presented text
      // without asking for a new code, and require a fresh explicit acceptance.
      if (error?.status === 409 || error?.code === 'TERMS_ACCEPTANCE_REQUIRED') {
        await refreshPendingChallengeLegal();
      }

    } finally {
      state.otpBusy = false;
      renderApp();
    }
  }

  async function handleAcceptTermsSubmit(form) {
    const sessionToken = state.session?.sessionToken;

    if (!sessionToken) {
      return;
    }

    const legalAcceptance = readLegalAcceptance(new FormData(form));

    if (!legalAcceptance) {
      state.legalAcceptError =
        'Veuillez lire et accepter les conditions générales d’utilisation (CGU) pour continuer.';
      renderApp();
      return;
    }

    state.legalAcceptBusy = true;
    state.legalAcceptError = '';
    renderApp();

    try {
      const legalStatus = await authService.acceptTerms({
        legalAcceptance,
        session: {
          sessionToken,
        },
      });

      // A logout during the request wins: nothing is applied to another session.
      if (state.session?.sessionToken !== sessionToken) {
        return;
      }

      state.legalStatus = legalStatus;
      state.legalStatusError = '';
      state.legalStatusStatus = 'ready';
      state.inboxStatus = 'idle';
      state.profileStatus = 'idle';
      state.sellerListingsStatus = 'idle';
      state.walletStatus = 'idle';
    } catch (error) {
      if (state.session?.sessionToken !== sessionToken) {
        return;
      }

      state.legalAcceptError =
        error instanceof Error
          ? error.message
          : 'Acceptation des conditions générales impossible.';

      if (error?.status === 409) {
        state.legalStatusStatus = 'idle';
        state.legalAcceptBusy = false;
        await loadLegalStatus();
        return;
      }
    } finally {
      if (state.session?.sessionToken === sessionToken) {
        state.legalAcceptBusy = false;
      }

      renderApp();
    }
  }

  async function handleIdentitySubmit(form) {
    if (!state.session) {
      return;
    }

    const formData = new FormData(form);
    const resolved = resolveDisplayNameForSubmit({
      displayName: formData.get('displayName'),
    });

    if (!resolved.ok) {
      state.identityError = resolved.error;
      state.identityMessage = '';
      renderApp();
      return;
    }

    state.identitySaveBusy = true;
    state.identityError = '';
    state.identityMessage = '';
    renderApp();

    try {
      const profile = await profileService.saveIdentity({
        displayName: resolved.value,
        session: state.session,
      });

      state.profile = profile;
      state.identitySaveBusy = false;
      state.identityMessage = `${profile.displayName || resolved.value}.`;
      renderApp();
    } catch (error) {
      state.identitySaveBusy = false;
      state.identityError =
        error instanceof Error ? error.message : "Impossible d'enregistrer le nom.";
      renderApp();
    }
  }

  async function handleProfileSubmit(form) {
    if (!state.session) {
      return;
    }

    const formData = new FormData(form);
    const areaSearch = String(formData.get('areaSearch') ?? '').trim();

    if (
      areaSearch &&
      (state.profileCityOptionsStatus === 'idle' || state.profileCityOptionsStatus === 'loading')
    ) {
      await loadProfileCityOptions();
    }

    const area = resolveProfileAreaForSubmit({
      area: formData.get('area'),
      areaSearch,
      cityOptions: state.profileCityOptions,
      selectedArea: state.profileSelectedArea,
    });

    if (area) {
      state.profileSelectedArea = area;
    }

    state.profileSaveBusy = true;
    state.profileError = '';
    state.profileMessage = '';
    renderApp();

    try {
      const profile = await profileService.saveProfile({
        area,
        session: state.session,
      });

      state.profile = profile;
      state.profileCityInput = profile.area;
      state.profileSelectedArea = profile.area;
      state.profileStatus = 'ready';
      state.profileSaveBusy = false;
      state.profileMessage = `Zone enregistrée : ${profile.area}.`;
      state.draft = syncDraftAreaFromProfile(state.draft, profile.area);
      if (state.draft) {
        draftStorage.saveDraft(state.draft);
      }
      renderApp();
    } catch (error) {
      state.profileSaveBusy = false;
      state.profileError =
        error instanceof Error ? error.message : 'Impossible de sauvegarder votre zone.';
      renderApp();
    }
  }

  async function handleProfileCitySuggestion(label) {
    state.profileCityBusy = true;
    state.profileError = '';
    state.profileMessage = '';
    renderApp();

    try {
      const city = await profileService.suggestCity({
        countryCode: resolvePhoneCountry(state.session?.phoneNumber),
        label,
      });

      if (!state.profileCityOptions.some((item) => item.label === city.label)) {
        state.profileCityOptions = [...state.profileCityOptions, city];
      }

      state.profileCityBusy = false;
      state.profileCityInput = city.label;
      state.profileSelectedArea = city.label;
      renderApp();
    } catch (error) {
      state.profileCityBusy = false;
      state.profileError =
        error instanceof Error ? error.message : 'Impossible d’ajouter cette ville.';
      renderApp();
    }
  }

  async function handlePublishSubmit() {
    if (!state.draft || !state.session) {
      return;
    }

    try {
      state.busyLabel = 'Synchronisation et publication en cours...';
      state.publishError = '';
      renderApp();

      const result = await submitLivePublish({
        apiBaseUrl: apiConfig.apiBaseUrl,
        draft: state.draft,
        fetchFn: apiFetch,
        session: state.session,
      });
      const listingRoute = buildBuyerListingRoute(result.outcome?.listingSlug);
      const shouldRetainDraft = shouldRetainDraftAfterPublish(result.outcome);

      state.busyLabel = '';
      state.publishedDraft = result.draft;
      if (shouldRetainDraft) {
        state.draft = draftStorage.saveDraft(result.draft);
      } else {
        draftStorage.clearDraft();
        state.draft = null;
      }
      state.publishOutcome = result.outcome;
      state.publishedListingRoute = listingRoute;
      state.publishedListingUrl =
        result.outcome?.listingSlug
          ? `/annonce/${result.outcome.listingSlug}/`
          : result.listingUrl || buildListingUrl(result.draft);
      state.sellerListingsStatus = 'idle';
      if (result.outcome?.status === 'approved') {
        buyerBrowseController.state.feedStatus = 'idle';
      }
      window.location.hash = '#success';
      if (
        result.outcome?.status === 'approved' &&
        !result.outcome.storyImageUrl &&
        result.outcome.listingSlug
      ) {
        startStoryImagePolling(result.outcome.listingSlug);
      }
    } catch (error) {
      state.busyLabel = '';
      state.publishError =
        error instanceof Error ? error.message : "Impossible d'envoyer l'annonce.";
      renderApp();
    }
  }

  function startStoryImagePolling(listingSlug) {
    const token = ++storyImagePollToken;
    const maxAttempts = 5;
    const intervalMs = 2000;

    const poll = async (attempt) => {
      if (token !== storyImagePollToken || getRoute().type !== 'success') {
        return;
      }

      try {
        const detail = await listingsService.getListingDetail(listingSlug, {
          session: state.session,
        });
        const storyImageUrl = detail?.storyImageUrl || '';

        if (storyImageUrl) {
          if (state.publishOutcome?.listingSlug === listingSlug) {
            state.publishOutcome = {
              ...state.publishOutcome,
              storyImageUrl,
            };
            renderApp();
          }

          return;
        }
      } catch {}

      if (attempt < maxAttempts && token === storyImagePollToken && getRoute().type === 'success') {
        window.setTimeout(() => {
          void poll(attempt + 1);
        }, intervalMs);
      }
    };

    window.setTimeout(() => {
      void poll(1);
    }, intervalMs);
  }

  async function handleBoost(listingId) {
    if (!state.session || !listingId) {
      return;
    }

    state.boostBusyListingId = listingId;
    state.boostMessage = '';
    renderApp();

    try {
      const result = await walletService.activateBoost({
        listingId,
        session: state.session,
      });

      state.boostBusyListingId = '';
      state.boostMessage = result.statusLabel;
      state.walletStatus = 'idle';
      state.sellerListingsStatus = 'idle';
      void loadWallet();
      void loadSellerListings();
      renderApp();
    } catch (error) {
      state.boostBusyListingId = '';
      state.boostMessage =
        error instanceof Error ? error.message : 'Impossible d’activer le boost.';
      renderApp();
    }
  }

  async function handleListingLifecycleAction({
    action,
    listingId,
    listingSlug,
  }) {
    if (!state.session || !listingId || !action) {
      return;
    }

    let reasonCode = '';

    if (action === 'mark_sold') {
      reasonCode = promptForLifecycleReason({
        choices: soldReasonChoices,
        message: 'Comment cette annonce a-t-elle été vendue ?',
      }) || '';

      if (!reasonCode) {
        return;
      }
    }

    if (action === 'delete') {
      reasonCode = promptForLifecycleReason({
        choices: deleteReasonChoices,
        message: 'Pourquoi supprimez-vous cette annonce ?',
      }) || '';

      if (!reasonCode) {
        return;
      }
    }

    if (
      ['pause', 'delete', 'mark_sold', 'restore', 'resume', 'relist'].includes(action) &&
      !window.confirm('Confirmer cette action sur votre annonce ?')
    ) {
      return;
    }

    state.listingLifecycleBusyId = listingId;
    state.listingLifecycleMessage = '';
    renderApp();

    try {
      const result = await sellerListingsService.applyLifecycleAction({
        action,
        listingId,
        reasonCode,
        session: state.session,
      });

      state.listingLifecycleBusyId = '';
      state.listingLifecycleMessage = buildLifecycleFeedbackMessage(action, result);
      state.sellerListingsStatus = 'idle';
      state.profileError = '';
      buyerBrowseController.state.feedStatus = 'idle';
      buyerBrowseController.state.detailStatus = 'idle';
      if (listingSlug) {
        state.currentListingSlug = listingSlug;
        void loadBuyerListing(listingSlug);
      }
      void loadSellerListings();
      void loadBuyerFeed();
      renderApp();
    } catch (error) {
      state.listingLifecycleBusyId = '';
      state.listingLifecycleMessage =
        error instanceof Error ? error.message : 'Impossible de mettre à jour cette annonce.';
      renderApp();
    }
  }

  async function handleEditListing(listingSlug) {
    if (!state.session || !listingSlug) {
      return;
    }

    try {
      const currentDetail = buyerBrowseController.state.detail;
      const detail =
        currentDetail?.slug === listingSlug && currentDetail?.editDraft
          ? currentDetail
          : await listingsService.getListingDetail(listingSlug, {
              session: state.session,
            });

      if (detail.viewerRole !== 'owner' || !detail.editDraft) {
        throw new Error("Impossible de préparer cette annonce pour modification.");
      }

      state.draft = syncDraftAreaFromProfile(
        createEditableListingDraft(detail.editDraft, {
          phoneNumber: state.session.phoneNumber,
        }),
        state.profile?.area ?? detail.editDraft.area ?? '',
      );
      draftStorage.saveDraft(state.draft);
      state.publishError = '';
      state.publishedDraft = null;
      state.publishOutcome = null;
      state.publishedListingRoute = '';
      state.publishedListingUrl = '';
      state.reviewErrors = [];
      window.location.hash = '#review';
    } catch (error) {
      window.alert(
        error instanceof Error
          ? error.message
          : "Impossible de préparer cette annonce pour modification.",
      );
    }
  }

  async function handleListingReviewSubmit(form) {
    const formData = new FormData(form);
    const slug = String(formData.get('slug') || state.currentListingSlug || '').trim();
    const rating = Number(formData.get('rating'));
    const comment = String(formData.get('comment') ?? '').trim();
    const messageTarget = form.querySelector('[data-review-message]');

    if (!slug) {
      return;
    }

    if (!state.session) {
      beginAuthIntent({
        returnRoute: `#listing/${encodeURIComponent(slug)}`,
        type: 'profile',
      });
      return;
    }

    if (messageTarget) {
      messageTarget.textContent = 'Envoi de votre avis...';
    }

    try {
      await reviewsService.submitReview({
        comment,
        rating,
        session: state.session,
        slug,
      });

      buyerBrowseController.state.detailStatus = 'idle';
      await loadBuyerListing(slug);
      renderApp();
    } catch (error) {
      if (messageTarget) {
        messageTarget.textContent =
          error instanceof Error ? error.message : 'Impossible d’envoyer votre avis.';
      }
    }
  }

  async function handleSellerReplySubmit(form) {
    if (!state.session) {
      beginAuthIntent({
        returnRoute: window.location.hash || '#buy',
        type: 'profile',
      });
      return;
    }

    const formData = new FormData(form);
    const reviewId = String(form.dataset.reviewId || '').trim();
    const reply = String(formData.get('sellerReply') ?? '').trim();
    const messageTarget = form.querySelector('[data-seller-reply-message]');
    const submitButton = form.querySelector('button[type="submit"]');

    if (!reviewId) {
      return;
    }

    if (messageTarget) {
      messageTarget.textContent = 'Envoi de votre réponse...';
    }

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    try {
      await sellerRepliesService.submitSellerReply({
        reply,
        reviewId,
        session: state.session,
      });

      state.sellerPublicError = '';
      state.sellerPublicStatus = 'idle';
      await loadPublicSeller(state.currentSellerId);
      renderApp();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Impossible d’envoyer votre réponse.';

      if (messageTarget) {
        messageTarget.textContent = message;
      } else {
        window.alert(message);
      }

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
    }
  }

  async function handleReviewReportSubmit(form) {
    if (!state.session) {
      beginAuthIntent({
        returnRoute: window.location.hash || '#buy',
        type: 'profile',
      });
      return;
    }

    const formData = new FormData(form);
    const reviewId = String(form.dataset.reviewId || '').trim();
    const reason = String(formData.get('reason') ?? '').trim();
    const submitButton = form.querySelector('button[type="submit"]');

    if (!reviewId) {
      return;
    }

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    try {
      await reviewReportsService.reportReview({
        reason,
        reviewId,
        session: state.session,
      });

      window.alert('Signalement envoyé. Merci pour votre aide.');
      state.sellerPublicError = '';
      state.sellerPublicStatus = 'idle';
      await loadPublicSeller(state.currentSellerId);
      renderApp();
    } catch (error) {
      window.alert(
        error instanceof Error ? error.message : 'Impossible de signaler cet avis.',
      );

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
    }
  }

  async function handleSendThreadMessage(form) {
    if (!state.session) {
      return;
    }

    const formData = new FormData(form);
    const body = String(formData.get('threadMessage') ?? '').trim();
    const threadId = form.dataset.threadId || state.currentThreadId;

    if (!body || !threadId) {
      return;
    }

    state.threadStatus = 'sending';
    state.threadDraftMessage = body;
    renderApp();

    try {
      const thread = await chatService.sendMessage({
        body,
        session: state.session,
        threadId,
      });

      state.thread = thread;
      state.threadStatus = 'ready';
      state.threadDraftMessage = '';
      state.inboxStatus = 'idle';
      void loadInbox();
      renderApp();
    } catch (error) {
      state.threadStatus = 'ready';
      state.threadError =
        error instanceof Error ? error.message : 'Impossible d’envoyer ce message.';
      renderApp();
    }
  }

  let shareReturnTarget = null;
  let shareLastAction = null;
  let shareLastAnnouncement = '';
  const shareAnnouncement = document.createElement('p');
  shareAnnouncement.className = 'app-share-menu__announcement';
  shareAnnouncement.dataset.shareAnnouncement = '';
  shareAnnouncement.setAttribute('role', 'status');
  shareAnnouncement.setAttribute('aria-live', 'polite');
  document.body.append(shareAnnouncement);
  const shareInertElements = new Map();
  const shareController = createListingShareController({
    baseUrl: window.location.origin,
    navigatorObject: navigator,
    fetchFn: apiFetch,
    openWindow(url) {
      // With the noopener feature, window.open returns null even on success.
      // Detach the blank same-origin window before navigating instead.
      const popup = window.open('about:blank', '_blank');
      if (!popup) return false;
      popup.opener = null;
      popup.location.replace(url);
      return true;
    },
    downloadFile(file) {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    },
    onChange(menu) { state.shareMenu = menu; renderApp(); },
  });

  function openShareMenu(trigger) {
    const candidates = [...appRoot.querySelectorAll('[data-action="open-share-menu"]')];
    shareReturnTarget = { slug: trigger.dataset.shareSlug || '', index: candidates.indexOf(trigger) };
    const detail = buyerBrowseController.state.detail;
    const matchingDetail = detail?.slug === trigger.dataset.shareSlug ? detail : null;
    const pending = shareController.start({
      slug: trigger.dataset.shareSlug || '',
      title: trigger.dataset.shareTitle || matchingDetail?.title || 'Annonce Zwibba',
      url: trigger.dataset.shareUrl || trigger.dataset.listingUrl || '',
      storyImageUrl: trigger.dataset.storyImageUrl || matchingDetail?.storyImageUrl || '',
      shareImageUrl: trigger.dataset.brandedImageUrl || matchingDetail?.shareImageUrl || '',
      primaryImageUrl: trigger.dataset.shareImageUrl || matchingDetail?.primaryImageUrl || '',
      // Only the post-publication success screen keeps the story mode.
      storyEnabled: trigger.dataset.shareContext === 'success',
    }, { mode: trigger.dataset.shareMode });
    const openedMenu = shareController.state;
    void pending.then(result => {
      if (shareController.state === openedMenu && ['handed-off', 'cancelled'].includes(result)) closeShareMenu();
    });
  }

  function closeShareMenu() {
    if (!state.shareMenu) return;
    shareController.close();
    for (const [element, wasInert] of shareInertElements) element.inert = wasInert;
    shareInertElements.clear();
    const candidates = [...appRoot.querySelectorAll('[data-action="open-share-menu"]')];
    const target = candidates[shareReturnTarget?.index];
    (target?.dataset.shareSlug === shareReturnTarget?.slug ? target :
      candidates.find(item => item.dataset.shareSlug === shareReturnTarget?.slug))?.focus({ preventScroll: true });
    shareReturnTarget = null;
    shareLastAction = null;
    shareAnnouncement.textContent = '';
    shareLastAnnouncement = '';
  }

  function syncShareFocus(previousAction) {
    const dialog = appRoot.querySelector('[role="dialog"][data-action="share-menu-sheet"]');
    if (!dialog) return;
    // Make siblings inert up to body, without hiding an ancestor of the dialog.
    let branch = dialog.closest('.app-share-menu');
    while (branch && branch !== document.body) {
      for (const sibling of branch.parentElement?.children || []) {
        if (sibling !== branch && sibling !== shareAnnouncement && !shareInertElements.has(sibling)) {
          shareInertElements.set(sibling, sibling.inert);
          sibling.inert = true;
        }
      }
      branch = branch.parentElement;
    }
    const focusable = [...dialog.querySelectorAll('button:not(:disabled), textarea')];
    if (previousAction && previousAction !== 'share-menu-sheet') shareLastAction = previousAction;
    const previous = focusable.find(item => item.dataset.action === previousAction) ||
      focusable.find(item => item.dataset.action === shareLastAction);
    (state.shareMenu.busy ? dialog : previous || focusable[0] || dialog).focus({ preventScroll: true });
    const message = state.shareMenu.message;
    if (message !== shareLastAnnouncement) {
      shareLastAnnouncement = message;
      shareAnnouncement.textContent = '';
      window.requestAnimationFrame(() => {
        if (state.shareMenu?.message === message) shareAnnouncement.textContent = message;
      });
    }
  }

  function closeMarketMenu({ restoreFocus = false } = {}) {
    const menu = appRoot.querySelector('[data-market-menu][open]');
    if (!menu) return;
    menu.open = false;
    if (restoreFocus) menu.querySelector('summary')?.focus({ preventScroll: true });
  }

  document.addEventListener('pointerdown', event => {
    const menu = appRoot.querySelector('[data-market-menu][open]');
    if (menu && !menu.contains(event.target)) closeMarketMenu();
  });

  document.addEventListener('focusin', event => {
    const menu = appRoot.querySelector('[data-market-menu][open]');
    if (menu && !menu.contains(event.target)) closeMarketMenu();
  });

  document.addEventListener('keydown', event => {
    if (!state.shareMenu) {
      if (event.key === 'Escape' && appRoot.querySelector('[data-market-menu][open]')) {
        event.preventDefault();
        closeMarketMenu({ restoreFocus: true });
      }
      return;
    }
    if (event.key === 'Escape') { event.preventDefault(); closeShareMenu(); return; }
    if (event.key !== 'Tab') return;
    const dialog = appRoot.querySelector('[role="dialog"][data-action="share-menu-sheet"]');
    const controls = [...(dialog?.querySelectorAll('button:not(:disabled), textarea') || [])];
    const index = controls.indexOf(document.activeElement);
    if (!controls.length) { event.preventDefault(); dialog?.focus(); return; }
    if (event.shiftKey && index <= 0) { event.preventDefault(); controls.at(-1).focus(); }
    else if (!event.shiftKey && (index < 0 || index === controls.length - 1)) { event.preventDefault(); controls[0].focus(); }
  });

  appRoot.addEventListener('click', async (event) => {
    const scrollTopTrigger = event.target.closest('[data-scroll-top-target]');

    if (scrollTopTrigger) {
      const scrollTopTarget = scrollTopTrigger.dataset.scrollTopTarget || '';
      const rootHash = `#${scrollTopTarget}`;

      pendingScrollReset.request(scrollTopTarget);

      if (window.location.hash === rootHash) {
        event.preventDefault();
        renderApp();
      }

      return;
    }

    const trigger = event.target.closest('[data-action]');

    if (!trigger) {
      return;
    }

    event.preventDefault();

    if (trigger.dataset.action === 'retry-buyer-feed') {
      const retry = loadBuyerFeed();
      renderApp();
      await retry;
      return;
    }

    if (trigger.dataset.action === 'filter-category') {
      const categoryId = trigger.dataset.categoryId || '';
      const nextCategoryId =
        buyerBrowseController.state.selectedCategoryId === categoryId ? '' : categoryId;

      buyerBrowseController.setSelectedCategoryId(nextCategoryId);
      renderApp();
      return;
    }

    if (trigger.dataset.action === 'discard-draft') {
      await handleDiscardDraft();
      return;
    }

    if (trigger.dataset.action === 'select-profile-city') {
      const cityLabel = trigger.dataset.cityLabel || '';
      state.profileCityInput = cityLabel;
      state.profileSelectedArea = cityLabel;
      state.profileError = '';
      state.profileMessage = '';
      if (!updateProfileCityField(
        {
          missingCityLabel: '',
          selectedArea: cityLabel,
          suggestions: [],
        },
        {
          focusInput: true,
          inputValue: cityLabel,
        },
      )) {
        renderApp();
      }
      return;
    }

    if (trigger.dataset.action === 'suggest-profile-city') {
      await handleProfileCitySuggestion(trigger.dataset.cityLabel || '');
      return;
    }

    if (trigger.dataset.action === 'submit-publish') {
      await handlePublishSubmit();
      return;
    }

    if (trigger.dataset.action === 'open-share-menu') {
      openShareMenu(trigger);
      return;
    }

    if (trigger.dataset.action === 'close-share-menu') {
      closeShareMenu();
      return;
    }

    if (trigger.dataset.action === 'share-menu-sheet') {
      return;
    }

    if (trigger.dataset.action === 'share-mode-post' || trigger.dataset.action === 'share-mode-story') {
      shareController.setMode(trigger.dataset.action === 'share-mode-story' ? 'story' : 'post');
      return;
    }
    if (trigger.dataset.action === 'retry-share-image') {
      await shareController.prepareImage();
      return;
    }
    const shareActions = {
      'share-native-link': 'native-link', 'share-native-image': 'native-image',
      'share-whatsapp-chat': 'whatsapp', 'share-facebook': 'facebook',
      'share-instagram': 'instagram', 'share-tiktok': 'tiktok',
      'copy-listing-link': 'copy-link', 'copy-share-caption': 'copy-caption',
      'download-story-image': 'download-image',
    };
    if (shareActions[trigger.dataset.action]) {
      await shareController.perform(shareActions[trigger.dataset.action]);
      return;
    }

    if (trigger.dataset.action === 'view-listing-link') {
      const listingRoute = trigger.dataset.listingRoute || '';

      if (listingRoute) {
        window.location.hash = listingRoute;
        return;
      }

      const listingUrl = trigger.dataset.listingUrl || buildListingUrl(state.draft);

      window.open(new URL(listingUrl, window.location.origin).toString(), '_blank', 'noopener');
      return;
    }

    if (trigger.dataset.action === 'begin-auth') {
      beginAuthIntent({
        returnRoute: trigger.dataset.returnRoute || '#sell',
        type: trigger.dataset.intent || 'profile',
      });
      return;
    }

    if (trigger.dataset.action === 'logout') {
      handleLogout();
      return;
    }

    if (trigger.dataset.action === 'retry-legal-status') {
      state.legalStatusStatus = 'idle';
      state.legalStatusError = '';
      state.legalAcceptError = '';
      renderApp();
      await loadLegalStatus();
      return;
    }

    if (trigger.dataset.action === 'accept-country-suggestion') {
      countryPreference.setStoredCountry('BE');
      void loadBuyerFeed();
      renderApp();
      return;
    }

    if (trigger.dataset.action === 'dismiss-country-suggestion') {
      countryPreference.setStoredCountry('CD');
      renderApp();
      return;
    }

    if (trigger.dataset.action === 'set-browse-country') {
      closeMarketMenu({ restoreFocus: true });
      countryPreference.setStoredCountry(trigger.dataset.country);
      void loadBuyerFeed();
      renderApp();
      return;
    }

    if (trigger.dataset.action === 'start-thread') {
      const listingId = trigger.dataset.listingId || '';
      const listingSlug = trigger.dataset.listingSlug || '';

      if (!state.session) {
        beginAuthIntent({
          listingId,
          listingSlug,
          returnRoute: `#listing/${encodeURIComponent(listingSlug)}`,
          type: 'message',
        });
        return;
      }

      await openThreadFromListing({
        listingId,
        listingSlug,
      });
      return;
    }

    if (trigger.dataset.action === 'select-listing-image') {
      state.selectedListingImageIndex = Number.parseInt(trigger.dataset.imageIndex || '0', 10) || 0;
      renderApp();
      return;
    }

    if (trigger.dataset.action === 'activate-boost') {
      if (!state.session) {
        beginAuthIntent({
          returnRoute: '#profile',
          type: 'profile',
        });
        return;
      }

      await handleBoost(trigger.dataset.listingId || '');
      return;
    }

    if (trigger.dataset.action === 'edit-listing') {
      await handleEditListing(trigger.dataset.listingSlug || '');
      return;
    }

    if (trigger.dataset.action === 'listing-lifecycle') {
      await handleListingLifecycleAction({
        action: trigger.dataset.lifecycleAction || '',
        listingId: trigger.dataset.listingId || '',
        listingSlug: trigger.dataset.listingSlug || state.currentListingSlug,
      });
    }
  });

  appRoot.addEventListener('input', (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (target.name === 'buyerSearch') {
      buyerBrowseController.setSearchQuery(target.value);
      renderApp();
      return;
    }

    if (target.name === 'areaSearch') {
      state.profileCityInput = target.value;
      state.profileError = '';
      state.profileMessage = '';
      const profileCityState = deriveProfileCityAutocompleteState({
        cityOptions: state.profileCityOptions,
        inputValue: target.value,
        selectedArea: state.profileSelectedArea,
      });
      state.profileSelectedArea = profileCityState.selectedArea;
      if (!updateProfileCityField(profileCityState)) {
        renderApp();
      }
      return;
    }

    if (target.name === 'threadMessage') {
      state.threadDraftMessage = target.value;
    }
  });

  appRoot.addEventListener('change', async (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement) {
      const form = target.form;

      if (form?.dataset.form === 'review-draft') {
        const hadReviewErrors = state.reviewErrors.length > 0;

        syncDraftFromReviewForm(form);

        if (target.name === 'categoryId' || target.name === 'fashionItemType') {
          renderApp();
          return;
        }

        if (hadReviewErrors) {
          renderApp();
          return;
        }

        if (target.name === 'priceCurrency') {
          syncReviewPriceUi(form);
          return;
        }
      }
    }

    if (!(target instanceof HTMLInputElement) || target.type !== 'file') {
      return;
    }

    const [file] = Array.from(target.files ?? []);

    target.value = '';

    if (!file) {
      return;
    }

    if (target.dataset.input === 'capture-first-photo') {
      const draftResetSerial = state.draftResetSerial;
      const task = photoUploadQueue.run(() => handleCapture(file, draftResetSerial));
      renderApp();
      await task;
      renderApp();
      return;
    }

    if (target.dataset.input === 'replace-primary-photo') {
      const draftResetSerial = state.draftResetSerial;
      const task = photoUploadQueue.run(() =>
        handlePrimaryPhotoReplacement(file, draftResetSerial),
      );
      renderApp();
      await task;
      renderApp();
      return;
    }

    if (target.dataset.input === 'guided-photo') {
      const draftResetSerial = state.draftResetSerial;
      const task = photoUploadQueue.run(() =>
        handleGuidedCapture(target.dataset.promptId || '', file, draftResetSerial),
      );
      renderApp();
      await task;
      renderApp();
    }
  });

  appRoot.addEventListener('submit', async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    event.preventDefault();

    if (form.dataset.form === 'review-draft') {
      await handleReviewSubmit(form);
      return;
    }

    if (form.dataset.action === 'submit-review') {
      await handleListingReviewSubmit(form);
      return;
    }

    if (form.dataset.action === 'submit-seller-reply') {
      await handleSellerReplySubmit(form);
      return;
    }

    if (form.dataset.action === 'report-review') {
      await handleReviewReportSubmit(form);
      return;
    }

    if (form.dataset.form === 'profile-identity') {
      await handleIdentitySubmit(form);
      return;
    }

    if (form.dataset.form === 'profile-zone') {
      await handleProfileSubmit(form);
      return;
    }

    if (form.dataset.form === 'request-otp') {
      await handlePhoneSubmit(form);
      return;
    }

    if (form.dataset.form === 'verify-otp') {
      await handleOtpSubmit(form);
      return;
    }

    if (form.dataset.form === 'accept-terms') {
      await handleAcceptTermsSubmit(form);
      return;
    }

    if (form.dataset.form === 'send-thread-message') {
      await handleSendThreadMessage(form);
    }
  });

  appRoot.addEventListener('input', (event) => {
    const target = event.target;

    if (
      !(
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
      )
    ) {
      return;
    }

    const form = target.form;

    if (!form || form.dataset.form !== 'review-draft') {
      return;
    }

    const hadReviewErrors = state.reviewErrors.length > 0;
    const shouldSyncDraft =
      target.name === 'priceAmount' ||
      target.name === 'priceCurrency' ||
      hadReviewErrors;

    if (shouldSyncDraft) {
      syncDraftFromReviewForm(form);
    }

    if (target.name === 'priceAmount' || target.name === 'priceCurrency') {
      if (hadReviewErrors) {
        renderApp();
        return;
      }

      syncReviewPriceUi(form);
      return;
    }

    if (hadReviewErrors) {
      renderApp();
    }
  });

  window.addEventListener('hashchange', () => {
    if (getRoute().type !== 'success') {
      storyImagePollToken += 1;
    }
    renderApp();
  });

  renderApp();
}
